import { useMemo, useState } from "react";
import { PageContainer } from "@/layouts/PageContainer";
import { DatePicker } from "@/components/ui/DatePicker";
import { StatCard } from "@/components/cards/StatCard";
import { ChartCard } from "@/components/cards/ChartCard";
import { LineAreaChart } from "@/components/charts/LineAreaChart";
import { SimpleBarChart } from "@/components/charts/SimpleBarChart";
import { LoadingState } from "@/components/feedback/LoadingState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { useAdminReport } from "@/hooks/useDashboard";
import { formatCompactINR, formatNumber, formatPercent } from "@/utils/format";

function isoDateDaysAgo(days: number) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export default function Reports() {
  const [from, setFrom] = useState(isoDateDaysAgo(30));
  const [to, setTo] = useState(isoDateDaysAgo(0));

  const report = useAdminReport({ from, to });

  const ridesSeries = useMemo(
    () => report.data?.ridesPerDay.map((p) => ({ label: p.date.slice(5), value: p.rides })) ?? [],
    [report.data],
  );
  const gmvSeries = useMemo(
    () => report.data?.ridesPerDay.map((p) => ({ label: p.date.slice(5), value: p.gmv })) ?? [],
    [report.data],
  );
  const zoneSeries = useMemo(
    () => report.data?.topZones.map((z) => ({ label: z.name, value: z.rides })) ?? [],
    [report.data],
  );

  return (
    <PageContainer
      title="Reports"
      description="Ride, revenue, and top-zone trends for a custom date range (last 7 days, last 30 days by default)."
      actions={
        <>
          <DatePicker value={from} onChange={setFrom} />
          <span className="self-center text-ink-faint">to</span>
          <DatePicker value={to} onChange={setTo} />
        </>
      }
    >
      {report.isLoading && <LoadingState label="Loading report…" />}
      {report.error && <ErrorState error={report.error} onRetry={() => report.refetch()} />}

      {report.data && (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard value={formatNumber(report.data.kpis.rides)} label="Rides" />
            <StatCard value={formatPercent(report.data.kpis.fulfilmentPct / 100)} label="Fulfilment" />
            <StatCard value={formatCompactINR(report.data.kpis.gmv)} label="GMV" />
            <StatCard value={formatCompactINR(report.data.kpis.commission)} label="Commission" highlight />
            <StatCard value={formatCompactINR(report.data.kpis.avgFare)} label="Avg fare" />
            <StatCard value={`${report.data.kpis.avgDistanceKm} km`} label="Avg distance" />
            <StatCard value={formatNumber(report.data.kpis.paymentMix.cash)} label="Cash rides" />
            <StatCard value={formatNumber(report.data.kpis.paymentMix.upi)} label="UPI rides" />
          </div>

          <ChartCard title="Rides per day (last 7 days)">
            <LineAreaChart data={ridesSeries} color="#0F6B5C" valueFormatter={formatNumber} />
          </ChartCard>

          <ChartCard title="GMV per day (last 7 days)">
            <LineAreaChart data={gmvSeries} color="#D9A125" valueFormatter={formatCompactINR} />
          </ChartCard>

          <ChartCard title="Top pickup zones (last 30 days)">
            <SimpleBarChart data={zoneSeries} />
          </ChartCard>
        </>
      )}
    </PageContainer>
  );
}
